import { CLAIM_TASKS } from './claim';

/** PostHog flag for task reminders (CUR-1276): the reminder field, the send job and the preference toggle. */
export const ENABLE_TASK_REMINDERS_FLAG = 'enable-task-reminders';

/**
 * The fixed "Set reminder" options (CUR-1276), in the order the field lists them. The values are
 * the `TaskReminderOffset` Prisma enum; the list is not org-configurable and day counts are not
 * stored ("1 month before" is a calendar month, resolved by the send job in the org's zone).
 */
export const TASK_REMINDER_OFFSETS = [
    'ON_THE_DAY',
    'ONE_DAY_BEFORE',
    'TWO_DAYS_BEFORE',
    'THREE_DAYS_BEFORE',
    'FIVE_DAYS_BEFORE',
    'SEVEN_DAYS_BEFORE',
    'TWO_WEEKS_BEFORE',
    'THREE_WEEKS_BEFORE',
    'ONE_MONTH_BEFORE',
] as const;
export type TaskReminderOffset = (typeof TASK_REMINDER_OFFSETS)[number];

export const TASK_REMINDER_OFFSET_LABELS = {
    ON_THE_DAY: 'On the day',
    ONE_DAY_BEFORE: '1 day before',
    TWO_DAYS_BEFORE: '2 days before',
    THREE_DAYS_BEFORE: '3 days before',
    FIVE_DAYS_BEFORE: '5 days before',
    SEVEN_DAYS_BEFORE: '7 days before',
    TWO_WEEKS_BEFORE: '2 weeks before',
    THREE_WEEKS_BEFORE: '3 weeks before',
    ONE_MONTH_BEFORE: '1 month before',
} as const satisfies Record<TaskReminderOffset, string>;

/**
 * The system-console `CommTemplate` every reminder email renders with (`internalName`, org
 * `SYSTEM_ORG_IDS.SYSTEM_CONSOLE`). Seeded by db-migration 0266; copy owed by product.
 */
export const TASK_REMINDER_TEMPLATE_INTERNAL_NAME = 'task_reminder';

/**
 * CUR-1280: the reminder every auto-created (system-generated) task starts with, editable like any
 * other selection. Task-list tasks follow their definition's own setting instead (CUR-1279).
 */
export const AUTO_CREATED_TASK_REMINDER_OFFSETS = ['ONE_DAY_BEFORE'] as const satisfies readonly TaskReminderOffset[];

/** Select options for the "Set reminder" field, in list order. */
export const TASK_REMINDER_OFFSET_OPTIONS = TASK_REMINDER_OFFSETS.map((value) => ({
    value,
    label: TASK_REMINDER_OFFSET_LABELS[value],
}));

/** The approver's task for a reserve change held under claims authority (CUR-1641). */
export const CLAIM_AUTHORITY_RESERVE_CHANGE_TASK = 'claim-authority:reserve-change';

/**
 * The requester's task after their held reserve change is declined (CUR-1642): it opens the
 * claim's History, where the reason is, and stays open until they complete it by hand.
 */
export const CLAIM_AUTHORITY_DECLINED_REVIEW_TASK = 'claim-authority:reserve-change-declined';

/**
 * The task of the person a payment approval step waits on (CUR-2671, PAY-T1), one per kind of
 * step: authorise, the final sign-off, and the referral to the insurer by the top of the raiser's
 * reporting line. Each completes when its step is decided (CUR-2673).
 */
export const CLAIM_PAYMENT_AUTHORISE_TASK = 'claim-payment:authorise';
export const CLAIM_PAYMENT_SIGN_OFF_TASK = 'claim-payment:sign-off';
export const CLAIM_PAYMENT_REFER_TASK = 'claim-payment:refer';

/**
 * The raiser's own tasks on a payment (CUR-2673, PAY-T1). "Correct returned payment" completes when
 * the payment is corrected and resent or withdrawn; "Mark payment as paid" when it is marked paid
 * (the raiser marks it while nobody holds the Accounts role, PAY-A9). Both open the payment.
 */
export const CLAIM_PAYMENT_CORRECT_TASK = 'claim-payment:correct';
export const CLAIM_PAYMENT_MARK_PAID_TASK = 'claim-payment:mark-paid';

/**
 * The raiser's task after their payment is declined (CUR-2673, PAY-A6): it opens the payment, where
 * the reason is, and stays open until they complete it by hand, as `CLAIM_AUTHORITY_DECLINED_REVIEW_TASK` does.
 */
export const CLAIM_PAYMENT_DECLINED_REVIEW_TASK = 'claim-payment:declined';

/**
 * The accounts step's task (CUR-2675, PAY-T2): one "Approve payment" for each person with the Accounts
 * role who may give the final approval, closed for all of them when one decides. Like "Mark payment as
 * paid" once anyone holds the role, it is a pool task: the payment finds it through `CompensationToTask`.
 */
export const CLAIM_PAYMENT_ACCOUNTS_APPROVE_TASK = 'claim-payment:accounts-approve';

const PAYMENT_STEP_OWNER_LOCKED_MESSAGE =
    'This task goes to the person the payment was sent to and cannot be reassigned on its own.';

const PAYMENT_RAISER_OWNER_LOCKED_MESSAGE =
    'This task goes to the person who raised the payment and cannot be reassigned on its own.';

// CUR-2675: marking a payment paid goes to everyone with the Accounts role, or to its raiser while nobody holds it.
const PAYMENT_MARK_PAID_OWNER_LOCKED_MESSAGE =
    'This task goes to whoever marks the payment as paid and cannot be reassigned on its own.';

/**
 * Tasks whose owner is the person a held reserve change (CUR-1641) or a payment approval step
 * (CUR-2671) waits on, keyed by `internalName`, with the refusal a reassignment gets: moving the
 * task alone would leave the request or the payment waiting on someone else. Bulk reassignment and
 * a claim's owner change leave them out (`excludeOwnerLockedTasksCondition`); `TaskService.update`
 * refuses them.
 */
export const OWNER_LOCKED_TASK_MESSAGES: ReadonlyMap<string, string> = new Map([
    [
        CLAIM_AUTHORITY_RESERVE_CHANGE_TASK,
        'This task goes to the person the reserve change was sent to and cannot be reassigned on its own.',
    ],
    [CLAIM_PAYMENT_AUTHORISE_TASK, PAYMENT_STEP_OWNER_LOCKED_MESSAGE],
    [CLAIM_PAYMENT_SIGN_OFF_TASK, PAYMENT_STEP_OWNER_LOCKED_MESSAGE],
    [CLAIM_PAYMENT_REFER_TASK, PAYMENT_STEP_OWNER_LOCKED_MESSAGE],
    [CLAIM_PAYMENT_CORRECT_TASK, PAYMENT_RAISER_OWNER_LOCKED_MESSAGE],
    [CLAIM_PAYMENT_MARK_PAID_TASK, PAYMENT_MARK_PAID_OWNER_LOCKED_MESSAGE],
    [
        CLAIM_PAYMENT_ACCOUNTS_APPROVE_TASK,
        'This task goes to everyone with the Accounts role and cannot be reassigned on its own.',
    ],
]);

/**
 * `Task.autoGenId` prefix of a claim's manual repair booking and allocate-manually tasks (CUR-1106).
 * Completing one records that a person booked the repair, so closing the claim cancels an open one
 * rather than completing it (CUR-1819).
 */
export const MANUAL_REPAIR_BOOKING_TASK_PREFIX = 'clear21-manual-booking:';

/**
 * Start of the System comment a claim close leaves on each task it completes (CUR-725), whatever the
 * closed status is called. Before CUR-1819 the close completed an open manual repair booking this way.
 */
export const CLOSED_CLAIM_TASK_COMMENT_PREFIX = 'Automatically completed: Claim ';

const PAYMENT_STEP_COMPLETION = {
    reason: 'This task completes when the payment is decided; it cannot be marked as done by hand.',
    pointer: { area: 'financials' },
} as const;

/**
 * Legacy catalogue of system tasks the platform completes on the user's behalf, keyed by
 * `internalName`.
 *
 * Manual completion is refused by `TaskService.update`; `task.getTask` resolves the
 * refusal into `manualCompletion` (reason plus pointer) so the task view can show a
 * disabled "Mark as done" button instead of hiding it (CUR-569). Evidence-based tasks
 * (CUR-1158) carry the same information on the row itself, so nothing new belongs here.
 */
export const TASKS_WITHOUT_MANUAL_COMPLETION = {
    [CLAIM_TASKS.updateClientOnProgress.internalName]: {
        reason: "This task cannot be completed manually. It completes automatically when a communication is logged to the claim's primary client contact, or when the claim is closed.",
        pointer: { area: 'communications' },
    },
    // CUR-1641: the approver's task for a held reserve change completes when the request is decided.
    [CLAIM_AUTHORITY_RESERVE_CHANGE_TASK]: {
        reason: 'This task completes when the reserve change is approved or declined; it cannot be marked as done by hand.',
        pointer: { area: 'financials' },
    },
    // CUR-2671: a payment approval step's task completes when the step is decided.
    [CLAIM_PAYMENT_AUTHORISE_TASK]: PAYMENT_STEP_COMPLETION,
    [CLAIM_PAYMENT_SIGN_OFF_TASK]: PAYMENT_STEP_COMPLETION,
    [CLAIM_PAYMENT_REFER_TASK]: PAYMENT_STEP_COMPLETION,
    // CUR-2675: the accounts approval completes, for everyone it went to, when one of them decides it.
    [CLAIM_PAYMENT_ACCOUNTS_APPROVE_TASK]: PAYMENT_STEP_COMPLETION,
    // CUR-2673: the raiser's tasks complete when they correct and resend, withdraw, or mark it paid.
    [CLAIM_PAYMENT_CORRECT_TASK]: {
        reason: 'This task completes when the payment is corrected and resent, or withdrawn; it cannot be marked as done by hand.',
        pointer: { area: 'financials' },
    },
    [CLAIM_PAYMENT_MARK_PAID_TASK]: {
        reason: 'This task completes when the payment is marked as paid; it cannot be marked as done by hand.',
        pointer: { area: 'financials' },
    },
} as const satisfies Record<string, { reason: string; pointer?: TaskCompletionPointer }>;

/**
 * Where the task view sends a handler who tries to tick a system-completed task by hand:
 * the claim area in which the completing work actually happens. Evidence-based tasks
 * (CUR-1158) carry one of these in their spec; the legacy catalogue above uses the same shape.
 */
export const TASK_COMPLETION_POINTER_AREAS = ['communications', 'financials'] as const;
export type TaskCompletionPointerArea = (typeof TASK_COMPLETION_POINTER_AREAS)[number];
export type TaskCompletionPointer = { area: TaskCompletionPointerArea };

export const SYSTEM_TASKS = {
    new_complaint: {
        internalName: 'new_complaint',
        title: 'Publish new obligation in your obligations register',
        relatedEntity: 'obligation',
        description:
            'A new obligation has been added to your obligations register from the Curium Obligations Library. It is currently in draft mode and requires further configuration by you.Also, it may have some suggested controls that you could implement to ensure this obligation is fulfilled by your organisation so be sure to pay close attention to this and add configure any controls you deem necessary.',
    },
    replacement_obligation: {
        internalName: 'replacement_obligation',
        title: 'Publish replacement obligation in your obligations register',
        relatedEntity: 'obligation',
        description:
            'A legislation, regulation or contract has changed. This new obligation will replace the old one on {{effectiveDate}} (of new obligation). It is time to publish this obligation. The old obligation will automatically expire on {{effectiveToDate}} (of old obligation). Original obligation: {{originalObligationLink}} New obligation: {{newObligationLink}}',
    },
    add_control_to_obligation: {
        internalName: 'add_control_to_obligation',
        title: 'Add a control to an obligation',
        relatedEntity: 'obligation',
        description:
            'This obligation does not have any controls. Add at least one control from the controls register or create a new one.',
    },
    add_control_to_risk: {
        internalName: 'add_control_to_risk',
        title: 'Add a control to a risk',
        relatedEntity: 'risk',
        description:
            'This risk does not have any controls. Add at least one control from the controls register or create a new one.',
    },
    publish_control: {
        internalName: 'publish_control',
        title: 'Publish control',
        relatedEntity: 'control',
        description:
            'This control is currently in draft status. Publish this control, ensuring you have scheduled tests associated with it.',
    },
    add_test_to_control: {
        internalName: 'add_test_to_control',
        title: 'Add test to control',
        relatedEntity: 'control',
        description:
            'This control does not currently have any scheduled control tests. Add a new test and/or schedule a test with a due date to ensure this control remains effective.',
    },
    rerun_failed_test: {
        internalName: 'rerun_failed_test',
        title: 'Re-run failed test',
        relatedEntity: 'control',
        description:
            'This test failed in its latest run. In order to ensure that the associated control remains effective you should make any necessary changes and re-run the test to ensure it passes.',
    },
    reconfigure_risk_matrix: {
        internalName: 'reconfigure_risk_matrix',
        title: 'Reconfigure risk matrix',
        relatedEntity: 'risk',
        description:
            'The risk matrix has been changed. You need to re-assess all existing risks that are currently configured with the old matrix according to the new matrix.',
    },
} as const;

export const TASK_STATUSES = {
    NOT_APPLICABLE: 'NOT_APPLICABLE',
    OVERDUE: 'OVERDUE',
    DUE_SOON: 'DUE_SOON',
    NOT_DUE: 'NOT_DUE',
    DONE: 'DONE',
    /**
     * A completed task whose `resolution` is CANCELLED: an evidence task the claims agent closed
     * at claim close without the evidence (CUR-1158). Every other completion, legacy null
     * resolution included, reads as DONE (CUR-1165).
     */
    CANCELLED: 'CANCELLED',
} as const;

export const TASK_STATUSES_LABELS = {
    NOT_APPLICABLE: '-',
    OVERDUE: 'Overdue',
    DUE_SOON: 'Due soon',
    NOT_DUE: 'Not due',
    DONE: 'Done',
    CANCELLED: 'Cancelled',
} as const;

export const TASK_DUE_DATE_SETTINGS = {
    DUE_SOON_DAYS_COUNT: 3,
    SYSTEM_TASK_BUSINESS_DAYS_COUNT: 5,
} as const;

/** Furthest a configured task due date may be offset from its creation anchor (10 years). */
export const MAX_TASK_DUE_DATE_OFFSET_DAYS = 10 * 365;

export const TASK_RELATED_ENTITIES = [
    'obligation',
    'complaint',
    'incident',
    'control',
    'risk',
    'claim',
    'msprProvider',
    'registerRecord',
] as const;

export const TASK_RELATED_ENTITIES_LABELS = {
    null: 'Ad-hoc',
    claim: 'Claim',
    complaint: 'Complaint',
    obligation: 'Obligation',
    risk: 'Risk',
    control: 'Control',
    incident: 'Incident',
    msprProvider: 'Material Service Provider',
    registerRecord: 'Register record',
} as const;

export type TaskStatus = keyof typeof TASK_STATUSES;
export type TaskRelatedEntity = (typeof TASK_RELATED_ENTITIES)[number];
